import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { toast } from "react-toastify";
import { X, AlertTriangle } from "lucide-react";

const MOTIVO_MAX = 500;

const formatearFechaHora = (fecha) =>
  fecha
    ? new Date(fecha).toLocaleString("es-CO", { dateStyle: "short", timeStyle: "short" })
    : "—";

// Confirmación de liberación administrativa del servicio activo de un usuario.
const ModalLiberarServicio = ({ open, onClose, usuario, onLiberado }) => {
  const [motivo, setMotivo] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const API = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");

  useEffect(() => {
    if (open) setMotivo("");
  }, [open]);

  if (!open || !usuario?.servicio) return null;

  const { servicio } = usuario;

  const liberar = async () => {
    if (!motivo.trim()) {
      toast.warning("Indica el motivo de la liberación");
      return;
    }
    try {
      setIsLoading(true);
      const res = await fetch(
        `${API}/admin/usuarios/${usuario._id}/liberar-servicio`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            cronogramaId: servicio.cronogramaId,
            motivo: motivo.trim(),
          }),
        },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      toast.success(data.message || "Servicio liberado");
      if (data.auditado === false) {
        toast.warning("La liberación se aplicó, pero no se pudo registrar en auditoría");
      }
      onLiberado();
      onClose();
    } catch (err) {
      toast.error(err.message || "No se pudo liberar el servicio");
      // El estado pudo cambiar (p.ej. el usuario finalizó): refrescar
      onLiberado();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="relative px-6 py-4 border-b">
          <h2 className="text-xl font-semibold text-gray-800">Liberar servicio</h2>
          <button
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-800"
            onClick={onClose}
            disabled={isLoading}
          >
            <X />
          </button>
        </div>

        <div className="p-6 space-y-4 text-sm">
          <div className="flex gap-3 bg-amber-50 border border-amber-200 rounded-xl p-3 text-amber-800">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <p>
              Este usuario tiene un servicio activo. ¿Está seguro de que desea
              liberar y cerrar esta gestión? El mantenimiento <strong>no</strong>{" "}
              se marcará como realizado: el cronograma conserva su estado y podrá
              abrirse de nuevo.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-gray-500">Usuario</p>
              <p className="text-gray-800 font-medium break-words">{usuario.name}</p>
              <p className="text-gray-500 text-xs break-words">{usuario.email}</p>
            </div>
            <div>
              <p className="text-gray-500">Servicio</p>
              <p className="text-gray-800 font-medium">
                {servicio.tipo || "—"} {servicio.equipo ? `· ${servicio.equipo}` : ""}
              </p>
              <p className="text-gray-500 text-xs break-all">{servicio.cronogramaId}</p>
            </div>
            <div>
              <p className="text-gray-500">Inicio</p>
              <p className="text-gray-800">{formatearFechaHora(servicio.inicio)}</p>
            </div>
            <div>
              <p className="text-gray-500">Estado del servicio</p>
              <p className="text-gray-800">{servicio.estadoCronograma}</p>
            </div>
          </div>

          <div>
            <label className="block text-gray-700 font-medium mb-1">
              Motivo de la liberación <span className="text-red-500">*</span>
            </label>
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value.slice(0, MOTIVO_MAX))}
              rows={3}
              placeholder="Ej.: el técnico perdió conexión y no puede cerrar el servicio"
              className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm"
            />
            <p className="text-xs text-gray-400 text-right">
              {motivo.length}/{MOTIVO_MAX}
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2 p-6 border-t">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button
            onClick={liberar}
            className="bg-red-600 text-white hover:bg-red-700"
            disabled={isLoading || !motivo.trim()}
          >
            {isLoading ? "Liberando..." : "Liberar servicio"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ModalLiberarServicio;
